/* @flow */
import * as React from 'react';
import ReactModal from 'react-modal';
import Row from 'components/Row';
import Text from 'components/Text';
import Heading from 'components/Heading';
import Link from 'components/Link';

import AdminIntegration from 'admin/components/AdminIntegration';
import AdminIntegrationsContainer from 'admin/containers/AdminIntegrationsContainer';
import AdminIntegratedToolsUpsellModal from 'admin/components/AdminIntegratedToolsUpsellModal';
import AdminRemindPlanIntegrationContainer from 'admin/containers/AdminRemindPlanIntegrationContainer.tsx';
import OrganizationIntegrationPermissions from 'admin/components/OrganizationIntegrationPermissions';
import PartnerIntegration from 'admin/components/PartnerIntegration';
import PartnerIntegrationModal from 'admin/components/PartnerIntegrationModal';

import { partition } from 'lodash';
import CurrentUser from 'auth/CurrentUser';

import './styles/AdminIntegratedToolsView.scss';

import LinkConstants from 'constants/LinkConstants';

type Props = {
  data: any,
  organizationUUID: string,
};

type State = {
  showPartnerIntegration?: *,
  showAdminControlledIntegrationUpsell?: *,
};

type IntegrationType = {
  organizationUUID: string,
  canEdit: boolean,
  onActionUnauthorized: void => void,
  toggleClickHandler?: void => void,
  isToggleable?: boolean,
  isEnabled?: boolean,
  providerKey: string,
};

class AdminIntegratedToolsView extends React.Component<Props, State> {
  state: State = {};

  showUpsellModal = (int: *) => this.setState({ showAdminControlledIntegrationUpsell: int });
  closeUpsellModal = () => this.setState({ showAdminControlledIntegrationUpsell: undefined });

  showPartnerModal = (int: *) => this.setState({ showPartnerIntegration: int });
  closePartnerModal = () => this.setState({ showPartnerIntegration: undefined });

  renderIntegrations(
    integrationArr: Array<IntegrationType>,
    params: { isToggleable: boolean, canEdit: boolean, parentUUID?: string }
  ): Array<React.Element> {
    const { canEdit, isToggleable, parentUUID } = params;
    const { organizationUUID } = this.props;

    return integrationArr.map(integration => {
      const { providerKey } = integration;
      return (
        <Row size="xsmall" key={providerKey}>
          {isToggleable ? (
            <AdminRemindPlanIntegrationContainer
              {...{
                integration,
                organizationUUID,
                parentUUID,
              }}
            />
          ) : (
            <AdminIntegration
              {...{
                integration,
                organizationUUID,
                canEdit,
              }}
              onActionUnauthorized={() => this.showUpsellModal(integration)}
            />
          )}
        </Row>
      );
    });
  }

  render() {
    const currentUser = CurrentUser.getUser();
    const can_toggle_VC_integrations =
      currentUser && currentUser.hasFeature('video_composer_integrations.web.jun-20');
    return (
      <OrganizationIntegrationPermissions organizationUUID={this.props.organizationUUID}>
        {({ canSee, canEdit, loading, parentId }) => {
          if (loading) {
            return null;
          }

          if (!canSee) {
            return <div>not found</div>;
          }

          return (
            <div>
              <ReactModal isOpen={!!this.state.showAdminControlledIntegrationUpsell}>
                <AdminIntegratedToolsUpsellModal
                  onRequestHide={this.closeUpsellModal}
                  organizationUUID={this.props.organizationUUID}
                  integration={this.state.showAdminControlledIntegrationUpsell}
                />
              </ReactModal>
              <ReactModal isOpen={!!this.state.showPartnerIntegration}>
                <PartnerIntegrationModal
                  onRequestHide={this.closePartnerModal}
                  integration={this.state.showPartnerIntegration}
                />
              </ReactModal>
              <AdminIntegrationsContainer
                fetchPolicy="cache-and-network"
                parentId={parentId}
                organizationUUID={this.props.organizationUUID}>
                {({ adminIntegrations, partnerIntegrations, parentUUID }) => {
                  const [remindPlanIntegrations, defaultIntegrations] = partition(
                    partnerIntegrations,
                    'isAdminToggleable'
                  );
                  return (
                    <>
                      <Row size="xxxlarge">
                        <Heading>App integrations</Heading>
                        <Row size="small">
                          <Text lineHeight="large" size="small" color="thunder" element="div">
                            Give your community access to these apps from the Remind composer.{' '}
                            <Link
                              href={LinkConstants.ADMIN_INTEGRATIONS_HELP}
                              target="_blank"
                              weight="semibold">
                              Learn More
                            </Link>
                          </Text>
                        </Row>

                        {can_toggle_VC_integrations && remindPlanIntegrations.length > 0 && (
                          <Row data-test="remind-plan-integrations">
                            <Row size="small">
                              <Text size="small" weight="semibold">
                                Remind plan integrations
                              </Text>
                              <Text lineHeight="large" size="small" color="thunder" element="div">
                                These integrations can be enabled for everyone in your organization.
                              </Text>
                            </Row>
                            {this.renderIntegrations(remindPlanIntegrations, {
                              isToggleable: true,
                              canEdit,
                              parentUUID,
                            })}
                          </Row>
                        )}

                        <Row data-test="admin-integrations">
                          <Row size="small">
                            <Text size="small" weight="semibold">
                              Premium integrations
                            </Text>
                            <Text lineHeight="large" size="small" color="thunder" element="div">
                              These integrations are available as add-ons to the Remind plan.
                            </Text>
                          </Row>
                          {this.renderIntegrations(adminIntegrations, {
                            isToggleable: false,
                            canEdit,
                          })}
                        </Row>
                      </Row>
                      <Row size="xxxlarge" data-test="partner-integrations">
                        <Text size="small" weight="semibold">
                          Default integrations
                        </Text>
                        <Row size="small">
                          <Text lineHeight="large" size="small" color="thunder" element="div">
                            These integrations are included for everyone in your organization.
                          </Text>
                        </Row>
                        {defaultIntegrations.map(integration => (
                          <PartnerIntegration
                            integration={integration}
                            organizationUUID={this.props.organizationUUID}
                            onClick={() => this.showPartnerModal(integration)}
                            key={integration.providerKey}
                          />
                        ))}
                      </Row>
                    </>
                  );
                }}
              </AdminIntegrationsContainer>
            </div>
          );
        }}
      </OrganizationIntegrationPermissions>
    );
  }
}

export default AdminIntegratedToolsView;
